import { Badge } from '@crowdfpl/ui-web/badge';
import { Button } from '@crowdfpl/ui-web/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@crowdfpl/ui-web/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@crowdfpl/ui-web/sheet';
import { Slider } from '@crowdfpl/ui-web/slider';
import { Switch } from '@crowdfpl/ui-web/switch';
import { SlidersHorizontal, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import type { PlayerDto } from '@/types/players';

const POSITIONS = [
  { value: 'GK', label: 'GK' },
  { value: 'DEF', label: 'DEF' },
  { value: 'MID', label: 'MID' },
  { value: 'FWD', label: 'FWD' },
] as const;

interface MobileFiltersBarProps {
  selectedPosition: string;
  setSelectedPosition: (v: string) => void;
  selectedTeam: string;
  setSelectedTeam: (v: string) => void;
  selectedPlayerA: PlayerDto | null;
  selectedPlayerB: PlayerDto | null;
  setSelectedPlayerA: (p: PlayerDto | null) => void;
  setSelectedPlayerB: (p: PlayerDto | null) => void;
  newDuelsActive: boolean;
  onNewDuelsToggle: () => void;
  myFplTeamActive: boolean;
  onMyFplTeamToggle: () => void;
  priceRange: [number, number];
  priceMinMax: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  onClearAll: () => void;
  teams: string[];
}

export function MobileFiltersBar({
  selectedPosition,
  setSelectedPosition,
  selectedTeam,
  setSelectedTeam,
  selectedPlayerA,
  selectedPlayerB,
  setSelectedPlayerA,
  setSelectedPlayerB,
  newDuelsActive,
  onNewDuelsToggle,
  myFplTeamActive,
  onMyFplTeamToggle,
  priceRange,
  priceMinMax,
  onPriceRangeChange,
  onClearAll,
  teams,
}: MobileFiltersBarProps): React.ReactElement {
  const [moreOpen, setMoreOpen] = useState(false);
  const isPriceFiltered = priceRange[0] !== priceMinMax[0] || priceRange[1] !== priceMinMax[1];

  const [sliderValue, setSliderValue] = useState<[number, number]>(priceRange);
  const [priceMin, priceMax] = priceRange;
  useEffect(() => {
    setSliderValue([priceMin, priceMax]);
  }, [priceMin, priceMax]);

  const hasActiveFilters =
    selectedPosition ||
    selectedTeam ||
    selectedPlayerA ||
    selectedPlayerB ||
    newDuelsActive ||
    myFplTeamActive ||
    isPriceFiltered;

  const moreCount = [newDuelsActive, myFplTeamActive, isPriceFiltered].filter(Boolean).length;

  return (
    <div className="space-y-2 py-2 bg-background/80 border-b border-border/20">
      {/* Row: Position, Team, More */}
      <div className="grid grid-cols-3 gap-2">
        <Select
          value={selectedPosition || 'all'}
          onValueChange={(v) => {
            trackUmamiEvent('duels_filter_change', { type: 'position', value: v === 'all' ? 'all' : v });
            setSelectedPosition(v === 'all' ? '' : v);
          }}
        >
          <SelectTrigger className="h-8 w-full min-w-0 rounded-full font-normal text-left pl-2.5 pr-1 text-xs [&>span]:text-xs truncate">
            <SelectValue placeholder="Position" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All positions</SelectItem>
            {POSITIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedTeam || 'all'}
          onValueChange={(v) => {
            trackUmamiEvent('duels_filter_change', { type: 'team', value: v === 'all' ? 'all' : v });
            setSelectedTeam(v === 'all' ? '' : v);
          }}
        >
          <SelectTrigger className="h-8 w-full min-w-0 rounded-full font-normal text-left pl-2.5 pr-1 text-xs [&>span]:text-xs truncate">
            <SelectValue placeholder="Team" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teams</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team} value={team}>{team}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className={cn(
            'h-8 w-full min-w-0 inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-normal border transition-colors cursor-pointer',
            moreCount > 0
              ? 'bg-secondary text-secondary-foreground border-secondary'
              : 'bg-background text-foreground border-input hover:bg-accent hover:text-accent-foreground',
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          More{moreCount > 0 && ` (${moreCount})`}
        </button>
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex justify-between items-center gap-2">
          <div className="flex flex-wrap gap-1 items-center">
            {newDuelsActive && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={onNewDuelsToggle}>
                New (24h) <X className="h-2 w-2" />
              </Badge>
            )}
            {myFplTeamActive && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={onMyFplTeamToggle}>
                My FPL Team <X className="h-2 w-2" />
              </Badge>
            )}
            {isPriceFiltered && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={() => onPriceRangeChange(priceMinMax)}>
                £{(priceRange[0] / 10).toFixed(1)}–{(priceRange[1] / 10).toFixed(1)}m <X className="h-2 w-2" />
              </Badge>
            )}
            {selectedPosition && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={() => setSelectedPosition('')}>
                {selectedPosition} <X className="h-2 w-2" />
              </Badge>
            )}
            {selectedTeam && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={() => setSelectedTeam('')}>
                {selectedTeam} <X className="h-2 w-2" />
              </Badge>
            )}
            {selectedPlayerA && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={() => setSelectedPlayerA(null)}>
                {selectedPlayerA.web_name} <X className="h-2 w-2" />
              </Badge>
            )}
            {selectedPlayerB && (
              <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer" onClick={() => setSelectedPlayerB(null)}>
                {selectedPlayerB.web_name} <X className="h-2 w-2" />
              </Badge>
            )}
          </div>
          <button onClick={onClearAll} className="h-6 px-2 text-xs shrink-0 text-muted-foreground hover:text-foreground cursor-pointer">
            Clear All
          </button>
        </div>
      )}

      {/* Bottom Sheet: More filters */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-xl px-4 pb-8 pt-4">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-base">More Filters</SheetTitle>
          </SheetHeader>

          <div className="space-y-5">
            {/* New Duels toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm">New Duels (24h)</span>
              <Switch checked={newDuelsActive} onCheckedChange={onNewDuelsToggle} />
            </div>

            {/* My FPL Team toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm">My FPL Team</span>
              <Switch checked={myFplTeamActive} onCheckedChange={onMyFplTeamToggle} />
            </div>

            {/* Price range */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Price range</span>
                <span className="text-xs text-muted-foreground">
                  £{(sliderValue[0] / 10).toFixed(1)}m – £{(sliderValue[1] / 10).toFixed(1)}m
                </span>
              </div>
              <div className="px-1">
                <Slider
                  min={priceMinMax[0]}
                  max={priceMinMax[1]}
                  step={1}
                  value={sliderValue}
                  onValueChange={(v) => setSliderValue(v as [number, number])}
                  onValueCommit={(v) => onPriceRangeChange(v as [number, number])}
                />
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>£{(priceMinMax[0] / 10).toFixed(1)}m</span>
                <span>£{(priceMinMax[1] / 10).toFixed(1)}m</span>
              </div>
            </div>

            {/* Clear all */}
            {(newDuelsActive || myFplTeamActive || isPriceFiltered) && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground cursor-pointer"
                onClick={() => {
                  if (newDuelsActive) onNewDuelsToggle();
                  if (myFplTeamActive) onMyFplTeamToggle();
                  if (isPriceFiltered) onPriceRangeChange(priceMinMax);
                }}
              >
                Clear all
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
